/**
 * Signed-URL thumbnails for private place photos.
 *
 * Display URLs come exclusively from repositories.places.getSignedImageUrl.
 * Public URLs are never constructed. Signed URLs are short-lived; a small
 * in-memory cache avoids re-signing on every render while staying well inside
 * the URL lifetime.
 */
import { useEffect, useState } from 'react'
import { repositories } from '@/services/repositories'
import type { Place } from '@/types'

const CACHE_TTL_MS = 5 * 60 * 1000

type CacheEntry = { url: string | null; fetchedAt: number }
const signedUrlCache = new Map<string, CacheEntry>()

function readCache(imagePath: string): string | null | undefined {
  const entry = signedUrlCache.get(imagePath)
  if (!entry) return undefined
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    signedUrlCache.delete(imagePath)
    return undefined
  }
  return entry.url
}

export function usePlaceThumbnail(place: Place): { url: string | null; loading: boolean } {
  const imagePath = place.imagePath ?? null
  const [url, setUrl] = useState<string | null>(() => (imagePath ? (readCache(imagePath) ?? null) : null))
  const [loading, setLoading] = useState<boolean>(() =>
    imagePath ? readCache(imagePath) === undefined : false,
  )

  useEffect(() => {
    if (!imagePath) {
      setUrl(null)
      setLoading(false)
      return
    }
    const cached = readCache(imagePath)
    if (cached !== undefined) {
      setUrl(cached)
      setLoading(false)
      return
    }
    let active = true
    setLoading(true)
    repositories.places
      .getSignedImageUrl(place)
      .then((signedUrl) => {
        signedUrlCache.set(imagePath, { url: signedUrl, fetchedAt: Date.now() })
        if (active) {
          setUrl(signedUrl)
          setLoading(false)
        }
      })
      .catch(() => {
        // Broken/expired signing is shown as 'no photo', never as a broken public URL.
        if (active) {
          setUrl(null)
          setLoading(false)
        }
      })
    return () => {
      active = false
    }
  }, [place, imagePath])

  return { url, loading }
}
